import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  SUPPORTED_CURRENCIES,
  CURRENCY_META,
  defaultCurrencyForLang,
  formatPrice as formatPriceWith,
  RATES_FROM_TRY,
} from "../lib/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ym_currency";

const CurrencyContext = createContext(null);

export function CurrencyProvider({ lang, children }) {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === "undefined") return defaultCurrencyForLang(lang);
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_CURRENCIES.includes(saved)) return saved;
    } catch {}
    return defaultCurrencyForLang(lang);
  });

  const [rates, setRates] = useState(RATES_FROM_TRY);
  const [ratesSource, setRatesSource] = useState("fallback");

  // Auto-follow language defaults if user hasn't manually picked
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setCurrencyState(defaultCurrencyForLang(lang));
    } catch {}
  }, [lang]);

  // Fetch live exchange rates (cached server-side 1h, fallback to fixed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/exchange-rates`, { timeout: 8000 });
        const live = res?.data?.rates;
        if (!cancelled && live && live.USD && live.EUR) {
          setRates({ TRY: 1, USD: Number(live.USD), EUR: Number(live.EUR) });
          setRatesSource(res?.data?.source || "live");
        }
      } catch {
        // Silently keep fallback rates
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((next) => {
    if (!SUPPORTED_CURRENCIES.includes(next)) return;
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const format = useCallback(
    (amountTry) => formatPriceWith(amountTry, currency, rates),
    [currency, rates]
  );

  const value = useMemo(() => ({
    currency,
    setCurrency,
    supported: SUPPORTED_CURRENCIES,
    meta: CURRENCY_META[currency],
    format,
    rates,
    ratesSource,
  }), [currency, setCurrency, format, rates, ratesSource]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be inside <CurrencyProvider>");
  return ctx;
}
